import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Grid2 from "@mui/material/Unstable_Grid2";
import { IWorkTimeType } from "../../models/workShiftTimeSlot/workTimeType";
import { styled } from "@mui/material/styles";
import { LoadingButton, LoadingButtonProps } from "../framework/loadingButton";

const GridButton = ({ sx = {}, ...props }: LoadingButtonProps) => {
    return <LoadingButton sx={{ py: 2, ...sx }} fullWidth {...props} />;
};

const StyledWorkTimeTypeButton = styled(GridButton)<LoadingButtonProps>(
    ({ theme }) => ({
        backgroundColor: theme.palette.white.main,
        "&:hover": {
            backgroundColor: theme.palette.white.main,
        },
        "&:active": {
            backgroundColor: theme.palette.white.main,
        },
    })
);

const SelectedWorkTimeTypeButton = styled(GridButton)<LoadingButtonProps>(
    ({ theme }) => ({
        color: "white",
        backgroundColor: theme.palette.success.main,
        "&:hover": {
            backgroundColor: theme.palette.success.main,
        },
        "&:active": {
            backgroundColor: theme.palette.success.main,
        },
    })
);

interface IWorkTimeTypeButtonProps {
    selected?: boolean;
}

type WorkTimeTypeButtonProps = IWorkTimeTypeButtonProps & LoadingButtonProps;

const WorkTimeTypeButton = ({
    selected = false,
    ...props
}: WorkTimeTypeButtonProps) => {
    return React.createElement(
        selected ? SelectedWorkTimeTypeButton : StyledWorkTimeTypeButton,
        {
            color: "success",
            variant: "outlined",
            ...props,
        }
    );
};

interface IWorkTimeTypeSelectProps {
    workTimeTypes: IWorkTimeType[];
    actionButtons?: LoadingButtonProps[];
    defaultValue?: string;
    value?: string;
    currentValue?: string; // from currently active work shfit
    onChange?: (value: string, prev?: string) => void;
    loadingWorkTimeTypeId?: string;
    disabled?: boolean;
    isChangeTaskAction?: boolean;
}

export const WorkTimeTypeSelect = ({
    workTimeTypes,
    value,
    currentValue,
    defaultValue,
    onChange,
    actionButtons = [],
    disabled = false,
    isChangeTaskAction,
}: IWorkTimeTypeSelectProps) => {
    const [selected, setSelected] = useState<string>(
        defaultValue || value || ""
    );
    const [confirmDisabled, setConfirmDisabled] = useState<boolean>(true);

    const handleChange = (id: string) => {
        const newValue = selected === id ? "" : id;
        if (value === undefined) {
            setSelected(newValue); // Uncontrolled input
        }
        if (onChange) {
            onChange(newValue, selected);
        }
    };

    useEffect(() => {
        if (value !== undefined) {
            setSelected(value); // Controlled input
        }
    }, [value]);

    useEffect(() => {
        if (((!selected && currentValue) || selected === currentValue) && !isChangeTaskAction) {
            setConfirmDisabled(true);
        } else {
            setConfirmDisabled(false);
        }
    }, [selected, currentValue, isChangeTaskAction]);

    return (
        <Box
            sx={{
                p: 2,
                height: "100%",
                width: "100%",
            }}
        >
            <Grid2 container spacing={2}>
                {actionButtons.map(({ key, ...rest }) => (
                    <Grid2 key={key} xs={6}>
                        <GridButton
                            {...rest}
                            disabled={confirmDisabled}
                        />
                    </Grid2>
                ))}
                {workTimeTypes?.length > 0
                    && workTimeTypes.map((t) => (
                        <Grid2 xs={6} key={t.workTimeTypeId}>
                            <WorkTimeTypeButton
                                selected={selected === t.workTimeTypeId}
                                onClick={() => handleChange(t.workTimeTypeId)}
                                disabled={disabled}
                            >
                                {t.name}
                            </WorkTimeTypeButton>
                        </Grid2>
                    ))}
            </Grid2>
        </Box>
    );
};
